import { Engine, Scene, ArcRotateCamera, Vector3, HemisphericLight, Mesh, MeshBuilder,
    ActionManager, ExecuteCodeAction, Color3, Color4,
    Material, StandardMaterial, Axis, CSG2, InitializeCSG2Async,
    Animation, Quaternion, TransformNode, AxesViewer, QuadraticEase, EasingFunction} from "@babylonjs/core";
import earcut from 'earcut';

import {Cart} from "./cart"
import {TileCoordinate, World} from "./world"
import { Tile } from "./tile";
import { AStar } from "./astar";


enum Direction {
    north = 1,
    east,    
    south,
    west,
}

enum RotationDirection {
    Clockwise = 1,
    CounterClockwise
}

class BoxedDirection {
    direction: Direction;

    static north() {
        return new BoxedDirection(Direction.north)
    }

    static south() {
        return new BoxedDirection(Direction.south)
    }

    static east() {
        return new BoxedDirection(Direction.east)
    }

    static west() {
        return new BoxedDirection(Direction.west)
    }

    constructor(direction: Direction) {
        this.direction = direction;
    }

    rotate(RotationDirectionParam: RotationDirection = RotationDirection.Clockwise) {
        if(RotationDirectionParam == RotationDirection.Clockwise) {
            switch(this.direction) { 
                case Direction.north:
                    this.direction = Direction.east
                    break;
                case Direction.east:
                    this.direction = Direction.south
                    break;
                case Direction.south:
                    this.direction = Direction.west
                    break;
                case Direction.west:
                    this.direction = Direction.north
                    break;
            }
        } else {
            switch(this.direction) { 
                case Direction.north:
                    this.direction = Direction.west
                    break;
                case Direction.east:
                    this.direction = Direction.north
                    break;
                case Direction.south:
                    this.direction = Direction.east
                    break;
                case Direction.west:
                    this.direction = Direction.south
                    break;
            }
        }
    }

    opposite() {
        switch(this.direction) { 
            case Direction.north:
                return Direction.south
            case Direction.east:
                return Direction.west
            case Direction.south:
                return Direction.north
            case Direction.west:
                return Direction.east
        }
    }

    static fromDelta(dx: number, dy: number) {
        if(dx == 0 && dy == 1) {
            return BoxedDirection.north();
        } else if(dx == 1 && dy == 0) {
            return BoxedDirection.east();
        } else if(dx == 0 && dy == -1) {
            return BoxedDirection.south();
        } else if(dx == -1 && dy == 0) {
            return BoxedDirection.west();
        }
    }
}

class LogicalTrack {
    terminals: BoxedDirection[]; // Entrance and exit
    x: number;
    y: number;
    orientation: BoxedDirection;
    supportsRotation: boolean;

    constructor(terminals, x, y, orientation, supportsRotation = false) {
        this.x = x;
        this.y = y;
        this.orientation = orientation;
        this.terminals = terminals;
        this.supportsRotation = supportsRotation;
    }

    rotate(RotationDirectionParam: RotationDirection = RotationDirection.Clockwise) {
        this.orientation.rotate(RotationDirectionParam);
        for(let terminal of this.terminals) {
            terminal.rotate(RotationDirectionParam);
        }
    }

    copy() {
        return new LogicalTrack(this.terminals.map(t => new BoxedDirection(t.direction)),
            this.x,
            this.y,
            new BoxedDirection(this.orientation.direction),
            this.supportsRotation);
    }

    versionRotateClockwise() : LogicalTrack {
        let track: LogicalTrack = this.copy();
        track.rotate();
        return track;
    }

    versionRotateCounterClockwise() : LogicalTrack {
        let track: LogicalTrack = this.copy();
        track.rotate(RotationDirection.CounterClockwise);
        return track;
    }

    getAdjacentTracks() : LogicalTrack[] {
        let x = this.x;
        let y = this.y;

        let adjacentTracks: LogicalTrack[] = [];
        let track = null;

        console.log(`Getting adjacent tracks for track at (${x}, ${y}) with terminals: ${this.terminals.map(t => t.direction)}`);

        if (this.supportsRotation) {
            adjacentTracks.push(this.versionRotateClockwise());
        }

        for (let terminal of this.terminals) {
            let dx = 0;
            let dy = 0;

            switch(terminal.direction) { 
                case Direction.north:
                    dy = 1;
                    break;
                case Direction.east:
                    dx = 1;
                    break;
                case Direction.south:
                    dy = -1;
                    break;
                case Direction.west:
                    dx = -1;
                    break;
            }

            if(x + dx >= World.SIZE || x + dx < 0 || y + dy >= World.SIZE || y + dy < 0) {
                console.log(`Out of bounds at (${x + dx}, ${y + dy})`);
                continue;
            }

            console.log(`Checking terminal direction: ${terminal.direction}, delta: (${dx}, ${dy})`);

            // Get track in direction
            track = globalThis.world.getTrack(new TileCoordinate(x + dx, y + dy));

            // Check if track exists
            if (!track) {
                console.log(`No track found at (${x + dx}, ${y + dy})`);
                continue;
            }

            let logicalTrack = track.logicalTrack;

            let deltaDirection = BoxedDirection.fromDelta(dx, dy);

            // Check if track has terminal in opposite direction
            if(logicalTrack.terminals.some(t => t.direction == deltaDirection.opposite())) {
                console.log(`Track found at (${x + dx}, ${y + dy}) with matching terminal`);
                adjacentTracks.push(logicalTrack);
            }

            if (logicalTrack.supportsRotation) { 
                let trackCopy = logicalTrack.versionRotateClockwise();
                if (trackCopy.terminals.some(t => t.direction == deltaDirection.opposite())) {
                    console.log(`RotatableStraightTrack found at (${x + dx}, ${y + dy}) with matching terminal after rotation`);
                    adjacentTracks.push(trackCopy);
                }
            }
        }

        console.log(`Adjacent tracks found: ${adjacentTracks.length}`);
        return adjacentTracks;
    }

    // Helper to get key for grid position
    private getKey(): string {
        return `${this.x},${this.y},${this.orientation.direction}`;
    }

    async astarSearch(goal: LogicalTrack) {
        let astar = new AStar<LogicalTrack>({
            getNeighbors: (pos) => {
                const validMoves = pos.getAdjacentTracks();
                // Each move costs 1 unit
                return validMoves.map(move => ({ state: move, cost: 1 }));
            },
            heuristic: (current, goal) => {
                // Manhattan distance - might not be perfectly admissible but works as an estimate
                return Math.abs(current.x - goal.x) + Math.abs(current.y - goal.y);
            },
            equals: (a, b) => a.x === b.x && a.y === b.y && a.orientation.direction === b.orientation.direction,
            getKey: (pos) => pos.getKey()
        });

        let cart = globalThis.world.cart;
        let path = astar.search(cart.track.logicalTrack, goal);

        if(!path) {
            console.log("No path found");
            return;
        }

        console.log(path.path);

        for(let track of path.path) {
            let currentTrack = cart.track.logicalTrack;

            // no-op
            if(currentTrack.x == track.x && currentTrack.y == track.y && currentTrack.orientation.direction == track.orientation.direction) {
                continue;
            }

            console.log("Moving to track", track);
            let visualTrack = globalThis.world.getTrack(new TileCoordinate(track.x, track.y));

            // The next track is the same as the current track. This must mean we
            // are on a turntable track. just rotate
            if(currentTrack.x == track.x && currentTrack.y == track.y) {

                // We are on a turntable track and we need to rotate
                if(currentTrack.supportsRotation && currentTrack.orientation.direction != track.orientation.direction) {
                    cart.box.setParent(visualTrack.turntable);
                    await visualTrack.rotateWithAnimation();
                    continue;
                }

                await cart.moveToTrack(visualTrack);
                continue;
                
            }

            // Get the actual state of the next track object
            let nextTrack = visualTrack.logicalTrack;

            // This means the next track is oriented correctly, we don't need to rotate it
            if(track.orientation.direction == nextTrack.orientation.direction) {
                await cart.moveToTrack(visualTrack);
                continue;
            }

            // If track is not oriented correctly, rotate it
            await visualTrack.rotateWithAnimation();
            await cart.moveToTrack(visualTrack);
            continue;
        }
    }
}

// Each track tile has an entrance, midpoint, and exit
// The cart will move from center to center of each tile
class Track {
    logicalTrack: LogicalTrack;
    box: Mesh;
    height: number
    width: number
    depth: number
    protected actionManager: any
    world: World;
    tile: Tile;
    railWidth: number = 0.1;
    railSpacing: number = 0.4;
    railHeight: number = 0.05;
    baseHeight: number = 0.1;
    scene: Scene;
    trackColor: Color3;

    constructor(scene, x, y, tile, world, terminals, orientation = BoxedDirection.north(), supportsRotation = false) {
        this.height = 1;
        this.width = 1;
        this.depth = 0.2;
        this.world = world;
        this.tile = tile;
        this.logicalTrack = new LogicalTrack(terminals, x, y, orientation, supportsRotation);

        this.scene = scene;

        this.trackColor = new Color3(0.5, 1.0, 0.5);

        let trackMat: StandardMaterial = new StandardMaterial('track', scene);
        trackMat.diffuseColor = this.trackColor;

        this.box = MeshBuilder.CreateBox("track", {height: this.height, width: this.width, depth: this.depth}, scene);
        this.box.position.x = 0;
        this.box.position.y = 0;
        this.box.position.z = this.depth/2;

        const axes = new AxesViewer(scene, 1);
        axes.xAxis.parent = this.box;
        axes.yAxis.parent = this.box;
        axes.zAxis.parent = this.box;

        this.box.material = trackMat;

        this.box.parent = tile.plane;

        this.actionManager = new ActionManager(scene);
        this.box.actionManager = this.actionManager;

        // On right click rotate
        this.box.actionManager.registerAction(
            new ExecuteCodeAction(ActionManager.OnRightPickTrigger, (evt) => {
                this.rotate();
            })
        );

        // On left click place cart
        this.box.actionManager.registerAction(
            new ExecuteCodeAction(ActionManager.OnLeftPickTrigger, (evt) => {
                if(this.world.mode === "find path") {
                    this.logicalTrack.astarSearch(this.world.getTrack(new TileCoordinate(this.logicalTrack.x, this.logicalTrack.y)).logicalTrack);
                    return;
                } else if (this.world.mode === "move cart") {
                    if (this.world.cart) {
                        this.world.cart.moveToTrack(this);
                    } else {
                        this.world.cart = new Cart(scene, x, y, this);
                    }
                }
            })
        );
    }

    // Rotate the track 90 degrees
    rotate(RotationDirectionParam: RotationDirection = RotationDirection.Clockwise) {
        if (RotationDirectionParam == RotationDirection.Clockwise) {
            this.box.rotate(Axis.Z, -Math.PI/2);
        } else {
            this.box.rotate(Axis.Z, Math.PI/2);
        }

        this.logicalTrack.rotate(RotationDirectionParam);
    }
}

// Static, straight track. Orientation of X or Y
class StraightTrack extends Track {
    constructor(scene, x, y, tile, world) {
        super(scene, x, y, tile, world, [BoxedDirection.east(), BoxedDirection.west()]);

        let railTile = this.createRailTile(scene);

        railTile.parent = this.box;
    }

    createRailTile(scene, options = {}) {
        // Default configuration
        const config = {
            tileSize: 1,                    
            railSpacing: 0.4,              
            railWidth: 0.1,                
            railHeight: 0.05,              
            baseHeight: 0.1,               
            ...options
        };
    
        const tile = new Mesh("railTile", scene);
    
        // Create straight rails using tubes
        const createStraightRail = (offsetX) => {
            const points = [
                new Vector3(-config.tileSize/2, 0, 0),
                new Vector3(config.tileSize/2, 0, 0)
            ];
    
            const rail = MeshBuilder.CreateTube("rail", {
                path: points,
                radius: config.railWidth / 2,
                cap: Mesh.CAP_ALL,
                updatable: false
            }, scene);
    
            rail.position.y = offsetX;
            rail.position.z = this.depth/2 + config.railWidth/2;
            // rail.position.y = config.baseHeight + (config.railHeight / 2);
            rail.parent = this.box;
            return rail;
        };
    
        // Calculate rail positions based on spacing
        const railOffset = config.railSpacing / 2;
    
        // Create left and right rails
        const leftRail = createStraightRail(-railOffset);
        const rightRail = createStraightRail(railOffset);

        const railMaterial = new StandardMaterial("railMat", scene);
        railMaterial.diffuseColor = new Color3(0.7, 0.7, 0.7);
        // railMaterial.metallic = 0.8;
        railMaterial.roughness = 0.2;
        leftRail.material = railMaterial;
        rightRail.material = railMaterial;
    
        // tile.railConfig = config;
        return tile;
    };
}

// Dynamic L-track - allows switching of tracks by rotation
class RightAngleTrack extends Track {
    constructor(scene, x, y, tile, world) {
        super(scene, x, y, tile, world, [BoxedDirection.south(), BoxedDirection.east()]);

        let curvedRailTile = this.createCurvedRailTile(scene);
        curvedRailTile.parent = this.box;

        curvedRailTile.parent = this.box;
        curvedRailTile.position.z = 0.1;
        curvedRailTile.rotate(Axis.X, -Math.PI/2);
    }

    createCurvedRailTile(scene, options = {}) {
        const config = {
            tileSize: 1,
            railSpacing: 0.4,
            railWidth: 0.1,
            railHeight: 0.05,
            baseHeight: 0.1,
            segments: 24,  // Number of segments in the curve
            ...options
        };
    
        const tile = new Mesh("curvedRailTile", scene);
    
        // // Create base
        // const base = MeshBuilder.CreateBox("base", {
        //     width: config.tileSize,
        //     height: config.baseHeight,
        //     depth: config.tileSize
        // }, scene);
        // base.position.y = config.baseHeight / 2;
        // base.parent = tile;
    
        // Create curved rails
        const createCurvedRail = (radius) => {
            const points = [];
            const quarterCircle = Math.PI / 2;
            
            for (let i = 0; i <= config.segments; i++) {
                const angle = (i / config.segments) * quarterCircle;
                const x = radius * Math.cos(angle);
                const z = radius * Math.sin(angle);
                points.push(new Vector3(x, 0, z));
            }
    
            const rail = MeshBuilder.CreateTube("rail", {
                path: points,
                radius: config.railWidth / 2,
                cap: Mesh.CAP_ALL,
                updatable: false
            }, scene);
    
            rail.position.y = -(config.railWidth / 2);
            return rail;
        };
    
        // Calculate rail radii
        const centerRadius = config.tileSize / 2;
        const innerRadius = centerRadius - (config.railSpacing / 2);
        const outerRadius = centerRadius + (config.railSpacing / 2);
    
        // Create inner and outer curved rails
        const innerRail = createCurvedRail(innerRadius);
        const outerRail = createCurvedRail(outerRadius);
    
        // Position rails in the corner
        innerRail.position.x = -config.tileSize / 2;
        innerRail.position.z = -config.tileSize / 2;
        outerRail.position.x = -config.tileSize / 2;
        outerRail.position.z = -config.tileSize / 2;
    
        innerRail.parent = tile;
        outerRail.parent = tile;

        const railMaterial = new StandardMaterial("railMat", scene);
        railMaterial.diffuseColor = new Color3(0.7, 0.7, 0.7);
        // railMaterial.metallic = 0.8;
        railMaterial.roughness = 0.2;
        innerRail.material = railMaterial;
        outerRail.material = railMaterial;
    
        // tile.railConfig = config;
        return tile;
    };
}

class RotatableStraightTrack extends Track {
    static csgInitialized = false;
    turntable: Mesh;

    constructor(scene, x, y, tile, world) {
        super(scene, x, y, tile, world, [BoxedDirection.east(), BoxedDirection.west()], BoxedDirection.north(), true);

        this.csg(scene);
    }

    createRailTile(scene, options = {}) {
        // Default configuration
        const config = {
            tileSize: 1,                    
            railSpacing: 0.4,              
            railWidth: 0.1,                
            railHeight: 0.05,              
            baseHeight: 0.1,               
            ...options
        };
    
        const tile = new Mesh("railTile", scene);
    
        // Base tile (square)
        const base = MeshBuilder.CreateBox("base", {
            width: config.tileSize,
            height: config.baseHeight,
            depth: config.tileSize
        }, scene);
        base.position.y = config.baseHeight / 2;
        base.parent = tile;
    
        // Create straight rails using tubes
        const createStraightRail = (offsetX) => {
            const points = [
                new Vector3(0, 0, -config.tileSize/2),
                new Vector3(0, 0, config.tileSize/2)
            ];
    
            const rail = MeshBuilder.CreateTube("rail", {
                path: points,
                radius: config.railWidth / 2,
                cap: Mesh.CAP_ALL,
                updatable: false
            }, scene);
    
            rail.position.x = offsetX;
            rail.position.y = config.baseHeight + (config.railHeight / 2);
            rail.parent = tile;
            return rail;
        };
    
        // Calculate rail positions based on spacing
        const railOffset = config.railSpacing / 2;
    
        // Create left and right rails
        const leftRail = createStraightRail(-railOffset);
        const rightRail = createStraightRail(railOffset);
    
        // Materials
        const baseMaterial = new StandardMaterial("baseMat", scene);
        baseMaterial.diffuseColor = new Color3(0.5, 0.5, 0.5);
        base.material = baseMaterial;
    
        const railMaterial = new StandardMaterial("railMat", scene);
        railMaterial.diffuseColor = new Color3(0.7, 0.7, 0.7);
        // railMaterial.metallic = 0.8;
        railMaterial.roughness = 0.2;
        leftRail.material = railMaterial;
        rightRail.material = railMaterial;
    
        // tile.railConfig = config;
        return tile;
    };

    // Create straight rails using tubes
    createStraightRail = (offsetY) => {
        const points = [
            new Vector3(-this.width/2, 0, 0),
            new Vector3(this.width/2, 0, 0)
        ];

        const rail = MeshBuilder.CreateTube("rail", {
            path: points,
            radius: this.railWidth / 2,
            cap: Mesh.CAP_ALL,
            updatable: false
        }, this.scene);

        rail.position.y = offsetY;
        rail.parent = this.box;

        return rail;
    };

    async csg(scene) {
        if(!RotatableStraightTrack.csgInitialized) {
            await InitializeCSG2Async(scene);
            RotatableStraightTrack.csgInitialized = true
        }

        let rail1 = this.createStraightRail(this.railSpacing/2);
        // rail1.position.z = this.depth/2 + this.railWidth;

        let rail2 = this.createStraightRail(-this.railSpacing/2);
        // rail2.position.z = this.depth/2 + this.railWidth;

        let cylinder = MeshBuilder.CreateCylinder("cylinder", {diameter: this.width, height: 1, tessellation: 24}, scene);
        cylinder.parent = this.box;
        cylinder.rotate(Axis.X, Math.PI/2);

        rail1.visibility = 0;
        rail2.visibility = 0;
        cylinder.visibility = 0;

        let rail1CSG = CSG2.FromMesh(rail1);
        let rail2CSG = CSG2.FromMesh(rail2);
        let cylinderCSG = CSG2.FromMesh(cylinder);

        // turntable.parent = this.box;
        const parentMesh = new TransformNode("parent", scene);
        parentMesh.parent = this.box;
        parentMesh.rotate(Axis.X, Math.PI/2);
        parentMesh.position.z = 0.1;

        let railsCSG = rail1CSG.add(rail2CSG);

        let turntable = railsCSG.intersect(cylinderCSG).toMesh("turntable");
        turntable.parent = parentMesh;

        // const axes = new AxesViewer(scene, 1);
        // axes.xAxis.parent = turntable;
        // axes.yAxis.parent = turntable;
        // axes.zAxis.parent = turntable;

        this.turntable = turntable;
        this.turntable.position.y = this.railWidth/2;

        let trackends1 = railsCSG.subtract(cylinderCSG).toMesh("trackends");
        trackends1.parent = parentMesh;
        trackends1.position.y = this.railWidth/2;

        let trackends2 = trackends1.clone("trackends2")
        trackends2.parent = parentMesh;
        trackends2.rotate(Axis.Y, Math.PI/2);
        // trackends2.position.y = this.depth/2 + this.railWidth/2;
    }

    async animateTurntableRotation(seconds=0.5) {
        const frameRate = 60;
        const totalFrames = frameRate * seconds; // 1 second animation

        const rotateAnimation = new Animation(
            "rotateAnimation",
            "rotation.y",
            frameRate,
            Animation.ANIMATIONTYPE_FLOAT,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );

        // Custom acceleration function
        const accelerationEasing = new QuadraticEase();
        accelerationEasing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

        let mesh = this.turntable;

        const keyFrames = [];
        keyFrames.push({
            frame: 0,
            value: mesh.rotation.y
        });
        keyFrames.push({
            frame: totalFrames,
            value: mesh.rotation.y + Math.PI / 2
        });

        rotateAnimation.setKeys(keyFrames);
        rotateAnimation.setEasingFunction(accelerationEasing);

        mesh.animations = [];
        mesh.animations.push(rotateAnimation);

        await new Promise<void>((resolve) => {
            this.scene.beginAnimation(mesh, 0, totalFrames, false, undefined, () => {
                resolve();
            });
        });

        // this.scene.beginAnimation(mesh, 0, totalFrames, false);
    }

    async rotateWithAnimation(seconds=0.5) {
        await this.animateTurntableRotation(seconds);
        this.logicalTrack.rotate();
    }
}

export {Track, StraightTrack, RightAngleTrack, RotatableStraightTrack};